import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useCampaignContext } from "../../../../../context/CampaignContext";
import useCampaignMutations from "../../../../../datahooks/useCampaignMutations";
import useCampaignValidation from "../../../../../hooks/useCampaignValidation";
import AssignSeats from "../../../../AssignSeats";
import CampaignScheduling from "../../../../CampaignScheduling";

interface CampaignStartProps {
  isOpen: boolean;
  isScheduled: boolean;
  onClose: () => void;
}

export default function CampaignStart({
  isOpen,
  isScheduled,
  onClose,
}: CampaignStartProps) {
  const navigate = useNavigate();
  const isStartingNowRef = useRef(true);

  const isDesktop = useTwBreakpoint("lg");
  const { id: campaignId, owners } = useCampaignContext();
  const { updateCampaign, isUpdatingCampaign } = useCampaignMutations();

  const { workerAccountsWithoutSeat } = useCampaignValidation(owners);

  const hasMissingLicenses = workerAccountsWithoutSeat.length > 0;
  const isStartingNow = isStartingNowRef.current;
  const buttonSize = isDesktop ? "md" : "lg";

  async function startCampaign(startNow: boolean) {
    isStartingNowRef.current = startNow;

    await updateCampaign({
      campaignId,
      updates: { state: startNow ? "ACTIVE" : "CREATED" },
    });

    navigate("../../details");
  }

  const Container = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  let content: React.ReactNode;
  if (hasMissingLicenses) {
    content = (
      <AssignSeats accounts={workerAccountsWithoutSeat} onClose={onClose} />
    );
  } else if (isScheduled) {
    content = (
      <CampaignScheduling
        campaignId={campaignId}
        onCancel={onClose}
        onSuccess={() => navigate("../../details")}
      />
    );
  } else {
    content = (
      <>
        <Title>Do you want to start this campaign?</Title>
        <Description className="mb-10">
          You can start this campaign immediately, or leave it as
          &quot;Ready&quot; and start it later if you want to make some last
          minute changes.
        </Description>

        <div className="flex flex-col gap-2 lg:flex-row-reverse lg:gap-4">
          <Button
            size={buttonSize}
            className="flex-1"
            onClick={() => startCampaign(true)}
            disabled={!isStartingNow && isUpdatingCampaign}
            isLoading={isStartingNow && isUpdatingCampaign}
          >
            Start campaign
          </Button>

          <Button
            size={buttonSize}
            variant="secondary-black"
            className="flex-1"
            onClick={() => startCampaign(false)}
            disabled={isStartingNow && isUpdatingCampaign}
            isLoading={!isStartingNow && isUpdatingCampaign}
          >
            Leave as Ready
          </Button>
        </div>
      </>
    );
  }

  return (
    <Container
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Content size={hasMissingLicenses || !isScheduled ? "md" : "lg"}>
        {content}
      </Content>
    </Container>
  );
}
